import { forwardRef, useRef, useCallback, useState, useMemo, useEffect, useImperativeHandle } from 'react'
import html2canvas from 'html2canvas'
import { QRCodeSVG } from 'qrcode.react'
import { formatPeriodRange } from '../../../utils/rentalPeriod'

const SHIPMENT_PRODUCT_ID = -10

function isAppleTouch() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return /iPhone|iPad|iPod/i.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function formatDate(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function isShipmentItem(item) {
  const productId = item?.productId ?? item?.product_id
  return Number(productId) === SHIPMENT_PRODUCT_ID
}

const OrderDetailPdfCard = forwardRef(function OrderDetailPdfCard({
  order,
  branch,
  stockMovements,
  onBusyChange,
}, ref) {
  const targetRef = useRef(null)
  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''
  const [busy, setBusy] = useState(false)

  const orderedItems = useMemo(() => {
    const items = Array.isArray(stockMovements) ? stockMovements : []
    const shipments = []
    const products = []
    for (const item of items) {
      if (isShipmentItem(item)) shipments.push(item)
      else products.push(item)
    }
    return [...shipments, ...products]
  }, [stockMovements])

  const hasShipment = useMemo(
    () => orderedItems.some((item) => isShipmentItem(item)),
    [orderedItems]
  )

  const total = orderedItems.reduce(
    (sum, it) => sum + Number(it.quantity || 0) * parseFloat(it.productPrice || 0),
    0
  )

  const captureOrderPngBlob = useCallback(async () => {
    const element = targetRef.current
    if (!element) return null

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#161b22',
      logging: false,
      height: element.scrollHeight,
      windowHeight: element.scrollHeight,
    })

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png')
    })
  }, [])

  const handleSaveImage = useCallback(async () => {
    if (busy) return
    try {
      setBusy(true)
      const blob = await captureOrderPngBlob()
      if (!blob) return

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = `order-${order?.id ?? 'order'}.png`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setBusy(false)
    }
  }, [busy, captureOrderPngBlob, order?.id])

  const handleShareImage = useCallback(async () => {
    if (busy) return
    setBusy(true)

    // Start capture immediately so iOS can attach the blob to the clipboard
    // while the tap is still a user gesture.
    const blobPromise = captureOrderPngBlob()

    const copyImage = () => {
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        return Promise.reject(new Error('clipboard-image-unavailable'))
      }
      return navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blobPromise.then((blob) => {
            if (!blob) throw new Error('empty-capture')
            return blob
          }),
        }),
      ])
    }

    try {
      if (isAppleTouch()) {
        try {
          await copyImage()
          window.alert('Изображение скопировано — вставьте его в нужное приложение.')
          return
        } catch {
          if (pageUrl && navigator.clipboard?.writeText) {
            try {
              await navigator.clipboard.writeText(pageUrl)
              window.alert('Ссылка скопирована.')
              return
            } catch {
              /* fall through */
            }
          }
          window.alert('Не удалось скопировать. Сохраните изображение.')
          return
        }
      }

      const blob = await blobPromise
      if (!blob) return

      const fileName = `order-${order?.id ?? 'order'}.png`
      const file = new File([blob], fileName, { type: 'image/png' })
      const title = order?.id != null ? `Заказ №${order.id}` : 'Заказ'
      const sharePayload = {
        title,
        text: pageUrl ? `${title}\n${pageUrl}` : title,
      }

      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ ...sharePayload, files: [file] })
            return
          }
          await navigator.share({ ...sharePayload, url: pageUrl || undefined })
          return
        } catch (e) {
          if (e?.name === 'AbortError') return
        }
      }

      try {
        await copyImage()
        window.alert('Изображение скопировано — вставьте его в нужное приложение.')
        return
      } catch {
        /* fall through */
      }

      window.alert('Не удалось открыть меню «Поделиться». Сохраните изображение.')
    } finally {
      setBusy(false)
    }
  }, [busy, captureOrderPngBlob, order?.id, pageUrl])

  useEffect(() => {
    onBusyChange?.(busy)
  }, [busy, onBusyChange])

  useImperativeHandle(ref, () => ({
    save: handleSaveImage,
    share: handleShareImage,
  }), [handleSaveImage, handleShareImage])

  return (
    <>
      <div
        ref={targetRef}
        className="order-pdf-target"
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
        }}
      >
        <div className="order-pdf-inner">
          <div className="client-order-detail-card order-pdf-card">
          <div className="client-order-detail-meta">
            <span className="client-order-detail-date">{formatDate(order?.createdAt)}</span>
            <div className="order-pdf-qr-wrap" aria-hidden>
              <QRCodeSVG value={pageUrl} size={56} level="M" includeMargin={false} />
            </div>
          </div>

          <section className="client-order-detail-section">
            <h3 className="client-order-detail-section-title">Клиент</h3>
            <div className="client-order-detail-row">
              <span className="client-order-detail-label">Имя</span>
              <span className="client-order-detail-value">{order?.customerName || '—'}</span>
            </div>
            <div className="client-order-detail-row">
              <span className="client-order-detail-label">Телефон</span>
              <span className="client-order-detail-value">{order?.customerPhone || '—'}</span>
            </div>
            {order?.customerDescription && (
              <div className="client-order-detail-row">
                <span className="client-order-detail-label">Комментарий</span>
                <span className="client-order-detail-value">{order.customerDescription}</span>
              </div>
            )}
          </section>

          <section className="client-order-detail-section">
            <h3 className="client-order-detail-section-title">Состав заказа</h3>
            <ul className="client-order-detail-items">
              {orderedItems.map((it) => {
                const isShipment = isShipmentItem(it)
                return (
                  <li
                    key={it.id}
                    className={`client-order-detail-item${isShipment ? ' client-order-detail-item--shipment' : ''}`}
                  >
                    <span className="client-order-detail-item-name">
                      {isShipment ? (
                        <span className="client-order-detail-shipmentPrefix">Доставка: </span>
                      ) : null}
                      {it.productName}
                      {it.fromAt || it.from_at ? (
                        <div>{formatPeriodRange(it.fromAt ?? it.from_at, it.toAt ?? it.to_at)}</div>
                      ) : null}
                    </span>
                    {!isShipment ? (
                      <span className="client-order-detail-item-qty">×{it.quantity}</span>
                    ) : null}
                    <span className="client-order-detail-item-price">
                      {parseFloat(it.productPrice || 0).toFixed(2)} сом
                    </span>
                  </li>
                )
              })}
            </ul>
            <div className="client-order-detail-total">
              <span>Итого</span>
              <span>{total.toFixed(2)} сом</span>
            </div>
          </section>

          {branch && !hasShipment && (
            <section className="client-order-detail-section client-order-detail-branch">
              <h3 className="client-order-detail-section-title">Адрес</h3>
              <div className="client-order-detail-address">{branch.name || '—'}</div>
            </section>
          )}
          </div>
        </div>
      </div>
    </>
  )
})

export default OrderDetailPdfCard
